import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import DashboardLayout from "../components/Layout/DashboardLayout";
import { DetailPageSkeleton } from "../components/ui/Skeleton";
import ClientForm, {
  ClientFormValues,
} from "../components/client/ClientForm";
import { clientsApi } from "../api/clientsApi";
import { getApiErrorPayload } from "../utils/apiError";
import { Client } from "../types";

const EditClientPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [client, setClient] = useState<Client | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    if (!id) {
      setLoadError("Missing client id.");
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await clientsApi.getById(id);
        if (cancelled) return;
        setClient(data);
      } catch (err: unknown) {
        if (cancelled) return;
        const payload = getApiErrorPayload(err);
        setLoadError(payload?.message ?? "Could not load this client.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const handleUpdate = async (values: ClientFormValues) => {
    if (!id || !client) return;

    const now = new Date().toISOString();

    // Preserve existing CROA timestamp; if a legacy client just accepted now,
    // stamp it with the current time.
    const wasSigned = client.croaDisclosureSigned;
    const croaDisclosureSigned = wasSigned || values.croaAccepted;
    const croaSignedAt = wasSigned
      ? client.croaSignedAt ?? null
      : values.croaAccepted
      ? now
      : null;

    const payload = {
      firstName: values.firstName.trim(),
      lastName: values.lastName.trim(),
      email: values.email.trim(),
      phone: values.phone.trim(),
      address: values.address.trim(),
      city: values.city.trim(),
      state: values.state.trim().toUpperCase(),
      zipCode: values.zipCode.trim(),
      ssnLast4: values.ssnLast4.replace(/\D/g, "").slice(0, 4),
      dateOfBirth: values.dateOfBirth,
      equifaxScore: values.equifaxScore,
      experianScore: values.experianScore,
      transUnionScore: values.transUnionScore,
      // Preserved from the fetched client — these fields are managed by other
      // flows (e-signature for serviceAgreement*, billing for stripeCustomerId,
      // operator status workflows for status/subscriptionStatus).
      status: client.status,
      subscriptionStatus: client.subscriptionStatus,
      stripeCustomerId: client.stripeCustomerId,
      croaDisclosureSigned,
      croaSignedAt,
      serviceAgreementSigned: client.serviceAgreementSigned,
      serviceAgreementSignedAt: client.serviceAgreementSignedAt ?? null,
      updatedAt: now,
    };

    try {
      await clientsApi.update(id, payload);
      navigate(`/clients/${id}`, { replace: true });
    } catch (err: unknown) {
      const apiPayload = getApiErrorPayload(err);
      throw new Error(apiPayload?.message ?? "Could not save changes.");
    }
  };

  return (
    <DashboardLayout>
      <div className="mx-auto max-w-3xl space-y-6">
        <div>
          <h1 className="text-2xl font-semibold text-white">Edit client</h1>
          <p className="text-sm text-slate-400">
            Update client profile, contact details, and bureau scores.
          </p>
        </div>

        {loading && <DetailPageSkeleton />}

        {!loading && loadError && (
          <div className="rounded-xl border border-red-500/40 bg-red-950/40 p-6 text-sm text-red-200">
            <p>{loadError}</p>
            <Link
              to="/clients"
              className="mt-3 inline-block rounded-lg border border-slate-700 px-4 py-2 text-sm font-medium text-slate-200 hover:bg-slate-800"
            >
              Back to clients
            </Link>
          </div>
        )}

        {!loading && !loadError && client && (
          <ClientForm
            mode="edit"
            initialValues={{
              firstName: client.firstName,
              lastName: client.lastName,
              email: client.email,
              phone: client.phone,
              address: client.address,
              city: client.city,
              state: client.state,
              zipCode: client.zipCode,
              ssnLast4: client.ssnLast4,
              dateOfBirth: client.dateOfBirth,
              equifaxScore: client.equifaxScore,
              experianScore: client.experianScore,
              transUnionScore: client.transUnionScore,
              croaAccepted: client.croaDisclosureSigned,
            }}
            submitLabel="Save changes"
            onSubmit={handleUpdate}
            onCancel={() => navigate(`/clients/${id}`)}
          />
        )}
      </div>
    </DashboardLayout>
  );
};

export default EditClientPage;
